import { queryOptions } from '@tanstack/react-query';
import { TWO_HOURS_IN_MILLISECONDS, TWO_MINUTES_IN_MILLISECONDS } from 'mdc-constants';
import type { ApiClient } from '../generated/laravel-typed-openapi';
import { favoriteKeys } from './favoriteKeys';
import { BOAT, type FavoritableType, fetchFavorites } from './favoritesApi';

/**
 * Options rather than a hook, so each app can pick how it consumes the same request — suspending
 * or not — without restating the cache policy.
 *
 * Fresh for two minutes: these are the caller's own favorites, and the only thing that changes them
 * is the caller, whose own mutations write the cache directly. Kept for two hours so paging back to
 * a boat list does not re-request. No refetch on window focus — a favorite does not change while
 * the tab is in the background.
 */
export const favoritesQueryOptions = (
  apiClient: ApiClient,
  { favoritableType = BOAT, enabled = true }: { favoritableType?: FavoritableType; enabled?: boolean } = {},
) =>
  queryOptions({
    queryKey: favoriteKeys.list(favoritableType),
    queryFn: ({ signal }) => fetchFavorites(apiClient, favoritableType, { signal }),
    // A signed-out visitor has no favorites to read, and asking would be a guaranteed 401 on every
    // boat list page. This is what callers gate on `isSignedIn`.
    enabled,
    staleTime: TWO_MINUTES_IN_MILLISECONDS,
    gcTime: TWO_HOURS_IN_MILLISECONDS,
    refetchOnWindowFocus: false,
  });
